<!--
  ProductPage.vue -- P6 storefront (Product page + size x quantity matrix).

  Card: Live Storefront - Home/Category/Product (ADR 0060/0061)
  https://trello.com/c/q7EQ3a0b
  Pebble: P6 -- Product page + size x quantity matrix (frontend).

  A single product at /shop/<storeSlug>/c/<categorySlug>/p/<productId>. Rendered
  inside StorefrontLayout via meta.layout='storefront'. Two-column sticky layout:

    StorefrontBreadcrumb  (Home / Category / Product)
    LEFT  gallery: thumbnail rail + main image (GarmentSilhouette front/back
          [side for caps] tinted to the selected color + labeled striped
          placeholders for on-model / flat-lay / logo-detail; a real per-color
          photo overrides the silhouette when present). On a product that HAS
          chosen images, a selected color with NONE shows the message "No
          preview available for this color" in place of the image and hides
          the (empty) thumbnail rail -- see `showNoPreview`.
    RIGHT buy box: style# / name / base price / color swatches / size x quantity
          matrix / add-to-cart (total + subtotal) / shipping+decoration note /
          Key features accordion (lede paragraph; key_features is [] for beta so
          NO iconed bullet list). NO star rating / reviews row (dropped for beta).
    "More in <Category>" rail: <=4 OTHER same-category products in sort_order.
    StorefrontFooter

  CART CONTRACT (Q8 -- money path): the cart ALREADY keys line items per
  (campaign_product_id, primary_attribute_value, secondary_attribute_value) --
  primary = color.id, secondary = size.id -- via Account.update_cart_quantity
  (a JSON cart blob on the account; the RPC merges/replaces by that exact tuple).
  So a multi-size add reuses the EXISTING single-line contract: one RPC call per
  size with qty > 0, each sharing the selected color. NO cart-contract / schema
  change was needed. update_cart_quantity is ABSOLUTE (sets the line to the
  given quantity), so we merge with any pre-existing line for the same variant
  before writing.

  Prices shown are ONE inclusive number per size (garment + decoration, Jake
  2026-08-19), in the SELECTED color: decoration is priced per color
  (size_prices_by_color; Jake 2026-10-22). NO decoration-included badge.

  There is deliberately NO note claiming decoration is "priced separately at
  checkout": Phil reported that as WRONG, and it was. Decoration pricing is
  being wired for real off a stitch count (Wilcom estimate at render;
  ArtisticPunch actuals arrive at fulfilment, AFTER checkout, so they cannot
  price a PDP). Until that lands, this surface makes NO claim about decoration
  cost rather than a false one. Do not re-add a claim here without the price
  behind it.

  Preview-safe: plain reads + the shopper cart RPC only; the parent
  StorefrontSessionGate owns the single token-establish point (?preview=1 skips
  the swap there and children never swap tokens).
-->
<template>
  <div class="sf-product" data-testid="sf-product-page">

    <div
      v-if="chromeLoading || productLoading"
      class="sf-product__loading"
      data-testid="sf-product-loading"
    >
      Loading. Please wait...
    </div>

    <div
      v-else-if="productError"
      class="sf-product__error"
      data-testid="sf-product-error"
    >
      {{ productError.message }}
    </div>

    <template v-else-if="product">
      <storefront-breadcrumb :trail="breadcrumbTrail" />

      <!-- Item 1: width-capped container, shared with Home/Category, so the
           buy-box layout doesn't run full-bleed on a wide monitor. -->
      <div class="sf-product__body sf-container">
      <div class="sf-product__grid" data-testid="sf-product-grid-layout">
        <!-- LEFT: gallery -->
        <div class="sf-product__gallery" data-testid="sf-product-gallery">
          <!-- Decision 16 DELETES this rail on mobile. It offered
               navigation between PLACEHOLDERS (per ADR 0061 most SKUs have
               no photography), so on a phone it spent a scarce row of
               screen on a control with nothing to show. It returns when
               per-SKU photography lands; desktop keeps it today. -->
          <div
            v-if="!isMobile && galleryFrames.length > 0"
            class="sf-product__thumbs"
            data-testid="sf-product-thumbs"
          >
            <button
              v-for="(frame, i) in galleryFrames"
              :key="i"
              type="button"
              class="sf-product__thumb"
              :class="{ 'is-active': i === activeFrame }"
              :data-testid="`sf-product-thumb-${i}`"
              :aria-pressed="i === activeFrame ? 'true' : 'false'"
              @click="activeFrame = i"
            >
              <img
                v-if="frame.type === 'photo'"
                :src="frame.url"
                :alt="`${product.name} - ${selectedColor.name}`"
                class="sf-product__thumb-photo"
                :data-testid="`sf-product-photo-${i}`"
              />
              <garment-silhouette
                v-else-if="frame.type === 'sil'"
                :silhouette="product.silhouette"
                :color="selectedColor"
                :view="frame.view"
                :photo-url="framePhotoUrl(frame)"
                pad="14%"
              />
              <span
                v-else
                class="sf-product__ph sf-product__ph--tiny"
                :data-testid="`sf-product-ph-${i}`"
                >{{ frame.label }}</span
              >
            </button>
          </div>

          <div class="sf-product__main" data-testid="sf-product-main-image">
            <!-- Imageless color on a product that has images (Jake,
                 2026-08-26): a message where the image would be, NOT a
                 silhouette and NOT a hidden swatch. -->
            <p
              v-if="showNoPreview"
              class="sf-product__no-preview"
              data-testid="sf-product-no-preview"
            >
              No preview available for this color
            </p>
            <img
              v-else-if="activeFrameObj.type === 'photo'"
              :src="activeFrameObj.url"
              :alt="`${product.name} - ${selectedColor.name}`"
              class="sf-product__main-photo"
              data-testid="sf-product-main-photo"
            />
            <garment-silhouette
              v-else-if="activeFrameObj.type === 'sil'"
              :silhouette="product.silhouette"
              :color="selectedColor"
              :view="activeFrameObj.view"
              :photo-url="framePhotoUrl(activeFrameObj)"
              pad="18%"
              data-testid="sf-product-main-silhouette"
            />
            <span
              v-else
              class="sf-product__ph"
              data-testid="sf-product-main-ph"
              >{{ activeFrameObj.label }}</span
            >
          </div>
        </div>

        <!-- RIGHT: buy box -->
        <div class="sf-product__buybox" data-testid="sf-product-buybox">
          <div
            v-if="product.style_number"
            class="sf-product__style"
            data-testid="sf-product-style"
          >
            Style: {{ product.style_number }}
          </div>

          <h1 class="sf-product__name" data-testid="sf-product-name">
            {{ product.name }}
          </h1>

          <div class="sf-product__price" data-testid="sf-product-price">
            {{ headlinePrice ?? pricePlaceholder }}
          </div>

          <!-- Colors -->
          <div
            v-if="colors.length > 0"
            class="sf-product__colors"
            data-testid="sf-product-colors"
          >
            <div class="sf-product__colors-label">
              <strong>Color:</strong>
              <span data-testid="sf-product-color-name">{{
                selectedColor.name
              }}</span>
            </div>
            <div class="sf-product__swatches">
              <button
                v-for="color in colors"
                :key="color.id"
                type="button"
                class="sf-product__swatch"
                :class="{ 'is-selected': color.id === selectedColorId }"
                :style="{ background: swatchHex(color) }"
                :aria-label="color.name"
                :aria-pressed="color.id === selectedColorId ? 'true' : 'false'"
                :data-testid="`sf-product-swatch-${color.id}`"
                @click="selectColor(color)"
              >
                <!-- Color name on hover via a styled tooltip, replacing the
                     laggy native ``title`` attribute. ``activator="parent"``
                     keeps the <button> itself as the hover/click/focus target
                     so @click, aria-label, aria-pressed and the testid are
                     untouched (accessibility + keyboard focus preserved). -->
                <v-tooltip activator="parent" location="top">
                  {{ color.name }}
                </v-tooltip>
              </button>
            </div>
          </div>

          <!-- Logo picker. Rendered whenever the selected color offers at
               least one logo; zero options means no control at all. A single
               option renders, preselected, only when the store's
               `show_single_logo` is on (see `showLogoPicker`). Options
               re-filter on color change via `reconcileLogoSelection`. -->
          <div
            v-if="showLogoPicker"
            class="sf-product__logos"
            data-testid="sf-product-logo-picker"
          >
            <div class="sf-product__logos-label">
              <strong>Logo</strong>
              <span
                v-if="selectedLogo"
                data-testid="sf-product-logo-name"
                >{{ selectedLogo.label }}</span
              >
            </div>
            <div class="sf-product__logos-options">
              <!-- Two handles per option, deliberately, and on two SEPARATE
                   elements -- the runner's selector grammar is exactly
                   `[data-testid=NAME]` (schema.py `_SELECTOR_SCHEMA`), with
                   no nth, combinator, or second-attribute form, so a shared
                   handle cannot share an element with an id-keyed one.
                   The button keeps `sf-product-logo-<id>` to address a
                   SPECIFIC logo; the label span carries the shared,
                   seed-agnostic `sf-product-logo-option` so a test can click
                   "an option" without hardcoding a DB autoincrement (the
                   fixture-id fragility tracked by Trello card RR8WnZ7Z).
                   Same two-element precedent as `sf-card` / `sf-card-media`
                   on the product grid, which exists for this exact reason. -->
              <button
                v-for="logo in eligibleLogos"
                :key="logo.id"
                type="button"
                class="sf-product__logo"
                :class="{ 'is-selected': logo.id === selectedLogoId }"
                :aria-label="logo.label"
                :aria-pressed="logo.id === selectedLogoId ? 'true' : 'false'"
                :data-testid="`sf-product-logo-${logo.id}`"
                :title="logo.file_number"
                :data-file-number="logo.file_number"
                @click="selectLogo(logo)"
              >
                <!-- Bounded tile, not a free-floating image: 7 of the 8
                     supplied AdventHealth assets have an OPAQUE background,
                     so an unboxed <img> bleeds a grey rectangle into the
                     page. A logo in a tile reads as intentional.

                     The tile is tinted to the ASSET'S OWN substrate, not to
                     the page: the dark-substrate art is light-on-dark, and a
                     white tile behind it would render a near-invisible white
                     mark. This also previews roughly how the logo reads on
                     the garment. `object-fit: contain` keeps the ~3:1 art
                     uncropped and undistorted. -->
                <span
                  class="sf-product__logo-tile"
                  :class="logoTileClass(logo)"
                  :data-testid="`sf-product-logo-tile-${logo.id}`"
                >
                  <img
                    v-if="logo.image_url"
                    :src="logo.image_url"
                    :alt="logo.label"
                    class="sf-product__logo-art"
                  />
                </span>
                <span
                  class="sf-product__logo-label"
                  data-testid="sf-product-logo-option"
                  >{{ logo.label }}</span
                >
              </button>
            </div>
          </div>

          <!-- Size x quantity matrix (B2B: order across sizes at once).

               A ONE-size product collapses to a bare quantity field: no
               size label, no per-size price (Jake, 2026-09-07, on the live
               AdventHealth storefront -- "Instead of 'OSFA' on items with
               one size, just include the quantity field and leave out the
               OSFA and price since both are redundant"). With a single
               size there is nothing to choose among, so the label names
               the only option there is and the per-size price restates
               `sf-product-price` a few lines above.

               `isSingleSize` keys on the COUNT, never on the label text --
               see the computed for why "OSFA" is the wrong thing to
               match. -->
          <div
            v-if="sizes.length > 0"
            class="sf-product__matrix"
            :class="{ 'is-single-size': isSingleSize }"
            data-testid="sf-product-matrix"
          >
            <!-- Two literal headings rather than one interpolated ternary
                 carrying an `&amp;` inside a string literal. Both render
                 identically (Vue's compiler decodes the entity either way,
                 verified) -- this form is simply the one whose correctness
                 a reader does not have to know that rule to check. -->
            <div
              class="sf-product__matrix-label"
              data-testid="sf-product-matrix-heading"
            >
              <strong v-if="isSingleSize">Quantity</strong>
              <strong v-else>Size &amp; quantity</strong>
            </div>
            <!-- MOBILE (decision 1): one full-width row per size, each
                 carrying the shared - / quantity / + control. A different
                 DOM from the desktop grid, not a restyling of it -- the
                 stepper buttons do not exist on desktop at all.

                 Jake killed the custom keypad (2026-09-10) and custom
                 size-to-size traversal (2026-09-11), so the quantity stays
                 a native number input the OS keyboard serves, with no
                 focus chaining and no auto-advance. See
                 docs/decisions/2026-09-11-mobile-quantity-entry-scope.md. -->
            <div v-if="isMobile" class="sf-product__size-rows">
              <div
                v-for="size in sizes"
                :key="size.id"
                class="sf-product__size-row"
                :data-testid="`sf-product-size-row-${size.id}`"
              >
                <div v-if="!isSingleSize" class="sf-product__size-row-meta">
                  <span
                    class="sf-product__size-row-label"
                    :data-testid="`sf-product-size-label-${size.id}`"
                    >{{ size.label }}</span
                  >
                  <span
                    v-if="sizePriceLabel(size)"
                    class="sf-product__size-row-price"
                    :data-testid="`sf-product-size-price-${size.id}`"
                    >{{ sizePriceLabel(size) }}</span
                  >
                  <span
                    v-if="isSizeUnavailable(size)"
                    class="sf-product__size-unavailable"
                    :data-testid="`sf-product-size-unavailable-${size.id}`"
                    >{{ sizeAvailabilityTip(size) || "Not available" }}</span
                  >
                  <!-- Touch devices have no hover, so the low-stock count is
                       visible text here (the tooltip below stays for mouse
                       users on a narrow window). Out-of-stock is the
                       "Not available in {color}" span above. -->
                  <span
                    v-else-if="sizeAvailabilityTip(size)"
                    class="sf-product__size-low-stock"
                    :data-testid="`sf-product-size-low-stock-${size.id}`"
                    >{{ sizeAvailabilityTip(size) }}</span
                  >
                  <!-- Color-specific availability hint: the size chip says why
                       the size is off, or how few are left. Text from
                       `sizeAvailabilityTip`; nothing renders for a size the
                       hint does not speak about (fail open). -->
                  <v-tooltip
                    v-if="sizeAvailabilityTip(size)"
                    activator="parent"
                    location="top"
                    :data-testid="`sf-product-size-tip-${size.id}`"
                  >
                    {{ sizeAvailabilityTip(size) }}
                  </v-tooltip>
                </div>
                <!-- The tooltip activator is this SPAN, not the control: a
                     disabled stepper fires no hover events, so a tooltip on
                     it would never open exactly when it is needed. -->
                <span
                  class="sf-product__qty-wrap sf-product__size-row-stepper"
                  :data-testid="`sf-product-qty-wrap-${size.id}`"
                >
                  <quantity-stepper
                    class="sf-product__size-row-stepper"
                    :model-value="
                      quantities[size.id] == null ? null : quantities[size.id]
                    "
                    :size-label="size.label"
                    :qty-testid="`sf-product-qty-${size.id}`"
                    :disabled="isSizeUnavailable(size)"
                    @update:model-value="onStepperQty(size.id, $event)"
                  />
                  <v-tooltip
                    v-if="sizeAvailabilityTip(size)"
                    activator="parent"
                    location="top"
                    :data-testid="`sf-product-qty-tip-${size.id}`"
                  >
                    {{ sizeAvailabilityTip(size) }}
                  </v-tooltip>
                </span>
              </div>
            </div>

            <!-- DESKTOP: the shipped grid, unchanged (ADR 0061). -->
            <div
              v-else
              class="sf-product__matrix-grid"
              data-testid="sf-product-matrix-grid"
            >
              <div
                v-for="size in sizes"
                :key="size.id"
                class="sf-product__matrix-cell"
                :data-testid="`sf-product-matrix-cell-${size.id}`"
              >
                <div v-if="!isSingleSize" class="sf-product__matrix-size">
                  <span
                    class="sf-product__matrix-size-label"
                    :data-testid="`sf-product-size-label-${size.id}`"
                    >{{ size.label }}</span
                  >
                  <span
                    v-if="sizePriceLabel(size)"
                    class="sf-product__matrix-size-price"
                    :data-testid="`sf-product-size-price-${size.id}`"
                    >{{ sizePriceLabel(size) }}</span
                  >
                  <span
                    v-if="isSizeUnavailable(size)"
                    class="sf-product__size-unavailable"
                    :data-testid="`sf-product-size-unavailable-${size.id}`"
                    >Not available</span
                  >
                  <!-- Color-specific availability hint: the size chip says why
                       the size is off, or how few are left. Text from
                       `sizeAvailabilityTip`; nothing renders for a size the
                       hint does not speak about (fail open). -->
                  <v-tooltip
                    v-if="sizeAvailabilityTip(size)"
                    activator="parent"
                    location="top"
                    :data-testid="`sf-product-size-tip-${size.id}`"
                  >
                    {{ sizeAvailabilityTip(size) }}
                  </v-tooltip>
                </div>
                <span
                  class="sf-product__qty-wrap"
                  :data-testid="`sf-product-qty-wrap-${size.id}`"
                >
                  <input
                    type="number"
                    min="0"
                    step="1"
                    inputmode="numeric"
                    placeholder="0"
                    class="sf-product__matrix-input"
                    :class="{ 'is-set': quantityFor(size.id) > 0 }"
                    :aria-label="
                      isSingleSize ? 'Quantity' : `Quantity for size ${size.label}`
                    "
                    :data-testid="`sf-product-qty-${size.id}`"
                    :disabled="isSizeUnavailable(size)"
                    :value="quantities[size.id] == null ? '' : quantities[size.id]"
                    @input="onQtyInput(size.id, $event)"
                  />
                  <v-tooltip
                    v-if="sizeAvailabilityTip(size)"
                  activator="parent"
                  location="top"
                  :data-testid="`sf-product-qty-tip-${size.id}`"
                >
                  {{ sizeAvailabilityTip(size) }}
                </v-tooltip>
                </span>
              </div>
            </div>
          </div>

          <!-- Add to cart (total qty + subtotal), then the added-state
               confirmation. One control, two states -- the confirmation lives
               IN the button rather than in a snackbar (Jake, 2026-08-29: the
               toast "covers the cart icon and profile menu").

               `:disabled` deliberately consults `addedToCart`: `canAddToCart`
               is false the moment the matrix clears, which a successful add
               does, so binding it alone would render an "Added - Go to Cart"
               button that cannot be clicked. -->
          <button
            type="button"
            class="sf-product__add"
            :class="{ 'is-added': addedToCart }"
            :disabled="(!canAddToCart && !addedToCart) || addingToCart"
            data-testid="sf-product-add-to-cart"
            @click="onAddButtonClick"
          >
            <span class="tf-icon">{{
              addedToCart ? "check_circle" : "shopping_cart"
            }}</span>
            <!-- The total sits in its own span, named for its state:
                 `sf-product-add-total` once it is exact (Fixed price, or the
                 quote for this selection came back), else
                 `sf-product-add-total-estimate`. The label text is
                 `addToCartLabel` either way. -->
            <span data-testid="sf-product-add-label"
              ><template v-if="labelShowsTotal"
                >Add to cart - {{ totalQty }} for <span
                  v-if="subtotalIsEstimate"
                  data-testid="sf-product-add-total-estimate"
                  >{{ totalText }}</span
                ><span v-else data-testid="sf-product-add-total">{{
                  totalText
                }}</span></template
              ><template v-else>{{ addToCartLabel }}</template></span
            >
          </button>
          <!-- Store pricing steps down with the LINE quantity, and this
               page's size prices are quantity-1, so it asks the quote
               endpoint for the selection at its real quantities and shows
               that exact total. If the quote fails (or a line has no price)
               the total stays an estimate, and this note says so. The cart
               is the price of record either way (#3884 gate P1-1). -->
          <p
            v-if="showEstimateNote"
            class="sf-product__estimate"
            data-testid="sf-product-subtotal-estimate"
          >
            Estimated at 1-unit prices. Your cart shows the final price for
            your quantity.
          </p>
          <!-- No size of this product has a server price (a Fixed-price
               product with no prices yet, or a pricing failure). The server
               would 409 the order anyway; say so up front (2026-09-29). -->
          <p
            v-if="noSizeAvailable"
            class="sf-product__unavailable"
            data-testid="sf-product-unavailable"
          >
            This product is not available to order right now.
          </p>

          <!-- Keep shopping: back to where the shopper came from. The target
               is the last LINKED breadcrumb crumb, so it is the category when
               one is linkable and the store home otherwise -- never a
               fabricated path. See `keepShoppingTarget`. -->
          <router-link
            class="sf-product__keep-shopping"
            :to="keepShoppingTarget"
            data-testid="sf-product-keep-shopping"
          >
            Keep Shopping
          </router-link>

          <!-- Key features accordion -->
          <div class="sf-product__features" data-testid="sf-product-features">
            <button
              type="button"
              class="sf-product__features-toggle"
              :aria-expanded="featuresOpen ? 'true' : 'false'"
              data-testid="sf-product-features-toggle"
              @click="featuresOpen = !featuresOpen"
            >
              <span>Key features</span>
              <span
                class="tf-icon sf-product__features-chevron"
                :class="{ 'is-open': featuresOpen }"
                aria-hidden="true"
                >expand_more</span
              >
            </button>
            <div
              v-if="featuresOpen"
              class="sf-product__features-body"
              data-testid="sf-product-features-body"
            >
              <p
                v-if="product.lede"
                class="sf-product__lede"
                data-testid="sf-product-lede"
              >
                {{ product.lede }}
              </p>
              <!-- key_features is [] for beta (no invented structured data);
                   render iconed bullets ONLY when the payload supplies them. -->
              <ul
                v-if="keyFeatures.length > 0"
                class="sf-product__feature-list"
                data-testid="sf-product-feature-list"
              >
                <li
                  v-for="(feature, i) in keyFeatures"
                  :key="i"
                  class="sf-product__feature-item"
                >
                  <span class="tf-icon">{{ feature.icon || "check" }}</span>
                  <span>{{ feature.label }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- "More in <Category>" rail: <=4 OTHER same-category products in
           sort_order, excluding the current one. Reuses the shared grid. -->
      <div
        v-if="relatedProducts.length > 0"
        class="sf-product__related"
        data-testid="sf-product-related"
      >
        <h2 class="sf-product__related-title" data-testid="sf-product-related-title">
          More in {{ relatedCategoryName }}
        </h2>
        <storefront-product-grid
          :products="relatedProducts"
          :store-slug="slug"
          :category-slug="categorySlug"
        />
      </div>
      </div>
    </template>

    <storefront-footer
      :policies="policies"
      :store-slug="slug"
      :white-label="whiteLabel"
    />

    <!-- Pinned action bar (decisions 11, 13), MOBILE ONLY.

         SCOPE NOTE -- this works against a documented seam on purpose.
         `.sf-product` is deliberately `min-height: 100%` with explicitly NO
         `position: fixed` (see the sticky-footer comment in the style block
         below: fixed "would overlay content on long pages"). That reasoning
         is about the FOOTER, which must follow long content; it does not
         apply to an action bar whose entire job is to stay visible while
         the shopper scrolls a long size list.

         Jake chose the smaller blast radius (2026-09-11): pin INSIDE the
         PDP rather than restructure StorefrontLayout, which every shopper
         page mounts. So the override lives here, scoped to the mobile
         media query, and desktop is untouched. `sf-product__spacer` below
         reserves the bar's height so the footer is never trapped under it.

         The CTA deliberately mirrors the in-flow button's `:disabled`
         expression, INCLUDING `addedToCart`. `canAddToCart` goes false the
         moment the matrix clears, which a successful add does -- binding
         it alone renders an "Added - Go to Cart" button that cannot be
         clicked. The design prototype's QActionBar drops this state. -->
    <div
      v-if="isMobile && product"
      class="sf-product__action-bar sf-product__action-bar--pinned"
      data-testid="sf-product-action-bar"
    >
      <div class="sf-product__action-bar-summary">
        <span
          v-if="totalQty > 0"
          class="sf-product__action-bar-units"
          data-testid="sf-product-bar-units"
          >{{ totalQty }} {{ totalQty === 1 ? "unit" : "units" }}</span
        >
        <span
          v-else
          class="sf-product__action-bar-empty"
          data-testid="sf-product-bar-empty"
          >No sizes selected yet</span
        >
        <strong
          v-if="totalQty > 0"
          class="sf-product__action-bar-total"
          data-testid="sf-product-bar-total"
          >{{ totalText }}</strong
        >
      </div>
      <button
        type="button"
        class="sf-product__add sf-product__action-bar-cta"
        :class="{ 'is-added': addedToCart }"
        :disabled="(!canAddToCart && !addedToCart) || addingToCart"
        data-testid="sf-product-bar-add-to-cart"
        @click="onAddButtonClick"
      >
        <span class="tf-icon">{{
          addedToCart ? "check_circle" : "shopping_cart"
        }}</span>
        <span data-testid="sf-product-bar-add-label">{{ addToCartLabel }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { resolveStorefrontSlug } from "@/router/storefrontSlug";
import { storefrontCategoryPath } from "@/utils/storefrontCategoryPath";
// Store-editor preview: cart writes are disabled (the account in scope is
// the editor's, not a shopper's). See the `isPreview` computed below.
import { isStorefrontPreview } from "@/router/storefrontPreview";
// Which account row owns this session's cart? A PRIVILEGED session (a
// back-office user, or an SA impersonating a distributor) is deliberately
// never swapped to a shopper token by router/privilegedSession.js, so it has
// no Vuex `state.account` to read an id from. The resolver falls back to the
// ACTOR's own account -- which is exactly the row the backend's
// `_assert_self_or_act_as` accepts as "self". See router/cartAccount.js.
import { resolveCartAccountId } from "@/router/cartAccount";
import { STOREFRONT_STORE_ID, cartStoreArgs } from "@/router/storefrontStoreId";
// `getActivePinia` guards the store lookup: this page mounts in contexts
// (and specs) where Pinia is not installed, and `useAccountStore()` throws
// there. Guarding degrades to the Vuex + localStorage signals instead.
import { getActivePinia } from "pinia";
import { useAccountStore } from "@/stores/account";
import {
  CURRENCY_EMPTY_PLACEHOLDER,
  formatCurrencyNumber,
} from "@/utils/currency";
import { sortSizes } from "@/constants/sizeOrder";
import { fromPriceLabel } from "@/pages/distributor/store-editor/StoreProducts/pricingMode";
import { useStorefrontChrome } from "@/composables/useStorefrontChrome";
import { useStorefrontProduct } from "@/composables/useStorefrontProduct";
import { useStorefrontSizeAvailability } from "@/composables/useStorefrontSizeAvailability";
import { useStorefrontCategory } from "@/composables/useStorefrontCategory";
import { hidesLoneLogo } from "@/utils/loneLogo";

import StorefrontBreadcrumb from "@/components/Storefront/StorefrontBreadcrumb.vue";
import StorefrontProductGrid from "@/components/Storefront/StorefrontProductGrid.vue";
import StorefrontFooter from "@/components/Storefront/StorefrontFooter.vue";
import GarmentSilhouette from "@/components/Storefront/GarmentSilhouette.vue";
import QuantityStepper from "@/components/Storefront/QuantityStepper.vue";
import { artworkKey } from "@/components/Storefront/cartLinePrices";
import { fetchStoreCart } from "@/components/Storefront/storeCart";
import {
  QUOTE_DEBOUNCE_MS,
  fetchPdpQuote,
} from "@/components/Storefront/pdpQuote";

export { QUOTE_DEBOUNCE_MS };

// The mobile breakpoint for the size x quantity control. Matches the
// existing `@media (max-width: 860px)` block at the bottom of this file --
// the two MUST stay in step, because this query decides which DOM renders
// and that one decides how it looks.
const MOBILE_MEDIA_QUERY = "(max-width: 860px)";

// Neutral swatch when a color row carries no hex (SanMar vendor_color_hex is
// nullable). Matches GarmentSilhouette's own default tint + the grid's.
const SWATCH_FALLBACK = "#c9d3db";

// The "More in <Category>" rail is capped at 4 (design: <=4 OTHER products).
const RAIL_CAP = 4;

// Caps (baseballCap / bucketHat) have a "side" second view; every other
// garment has a "back" second view. Mirrors GarmentSilhouette's own CAPS set.
const CAP_SILHOUETTES = new Set(["baseballCap", "bucketHat"]);

// Normalize a color name/id for matching (trim + collapse internal
// whitespace + lowercase). Guards against the whitespace/case bug class this
// codebase has repeatedly hit on SanMar color names ("Black Heather " with a
// trailing space -- see #2362/#2366/#2375). NEVER used as the value we store
// or render -- only for comparison; the product's own canonical color.id is
// always what gets set as state.
function normalizeColorKey(value) {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

export default {
  name: "StorefrontProductPage",

  inject: {
    // The store the page is for: the id StorefrontSessionGate resolved from
    // the URL slug (router/storefrontStoreId.js). The cart add names it, so the
    // server adds to THIS store and refuses a product of another.
    gateStoreId: { from: STOREFRONT_STORE_ID, default: null },
  },

  components: {
    "storefront-breadcrumb": StorefrontBreadcrumb,
    "storefront-product-grid": StorefrontProductGrid,
    "storefront-footer": StorefrontFooter,
    "garment-silhouette": GarmentSilhouette,
    "quantity-stepper": QuantityStepper,
  },

  setup() {
    // Chrome resolves brand color + store name -> commits to Vuex so the
    // shared topbar themes (same as the Category page).
    const {
      loading: chromeLoading,
      fetchChrome,
      // Tax & Legal policy URLs -> the footer's optional legal links.
      policies,
      // White-label branding -> the footer's brand mark
      // (card https://trello.com/c/mq26K91V).
      whiteLabel,
    } = useStorefrontChrome();
    const {
      product,
      loading: productLoading,
      error: productError,
      fetchProduct,
    } = useStorefrontProduct();
    // The background "not available in this color" hint. A separate instance,
    // fail-open by construction (see the composable).
    const { availability: sizeAvailability, fetchAvailability } =
      useStorefrontSizeAvailability();
    // The rail reuses the category endpoint (its `products[]` are already in
    // sort_order); a SEPARATE composable instance so its fetches never race the
    // product fetch.
    const { category: relatedCategory, fetchCategory: fetchRelatedCategory } =
      useStorefrontCategory();

    // The ACTOR's own account id (the bearer-token owner), used to resolve
    // which cart this session writes when there is no shopper session --
    // i.e. a privileged / impersonating session. Guarded because this page
    // also mounts without Pinia installed; `accountStore` stays null there
    // and the resolver falls back to its other signals.
    const accountStore = getActivePinia() ? useAccountStore() : null;

    return {
      accountStore,
      chromeLoading,
      policies,
      whiteLabel,
      fetchChrome,
      product,
      productLoading,
      productError,
      fetchProduct,
      relatedCategory,
      fetchRelatedCategory,
      sizeAvailability,
      fetchAvailability,
    };
  },

  data() {
    return {
      // TRUE at the mobile breakpoint, where the size x quantity matrix
      // renders as one row per size instead of the desktop grid.
      //
      // Deliberately a REACTIVE FLAG rather than pure CSS. The mobile
      // treatment is a different DOM (a stepper per row, a pinned action
      // bar), not a restyling of the same DOM -- and a flag is what lets
      // the layout be asserted behaviourally, since jsdom applies no media
      // queries. `matchMedia` is absent in jsdom, hence the guard in
      // `syncIsMobile`.
      isMobile: false,
      mobileMediaQuery: null,
      // Selected color id (primary attribute value). Defaults to the first
      // color once the product loads.
      selectedColorId: null,
      // Selected logo (artworks.id) for the shopper-facing logo picker.
      // Null when the product offers no logo for the current color, or
      // when more than one is offered and the shopper has not chosen yet.
      selectedLogoId: null,
      // { [sizeId]: quantity } -- the size x quantity matrix state.
      quantities: {},
      // Active gallery frame index (thumbnail selection).
      activeFrame: 0,
      featuresOpen: true,
      addingToCart: false,
      // True from a SUCCESSFUL add until the shopper has something pending to
      // add again (a new quantity, a different color, a different product).
      // While true the button reads "Added - Go to Cart" and NAVIGATES; the
      // dedicated flag is what makes "did the last add succeed" a single
      // source of truth rather than something inferred from an empty matrix
      // (which is also the state of a page nobody has touched).
      addedToCart: false,
      // The last SETTLED quote of the selection at its real quantities:
      // `{ key, unitBySize }`, where `unitBySize` is null when the quote
      // failed or a line had no price. Used only while `key` equals the
      // current `quoteKey`, so a quote can never outlive its selection.
      quote: null,
      // Debounce handle and request sequence for the quote (see `quoteKey`).
      quoteTimer: null,
      quoteSeq: 0,
    };
  },

  computed: {
    slug() {
      return resolveStorefrontSlug(this.$route) || "";
    },

    categorySlug() {
      return this.$route.params.categorySlug || "";
    },

    productId() {
      return this.$route.params.id || this.$route.params.productId || "";
    },

    // The raw ?color= query param (shareable deep-link, Boulders: Storefront
    // color deep-link + QR share code, https://trello.com/c/PWUeRl0u).
    // Resolution against the product's actual colors happens where the
    // product payload is available (the `product` watcher); this computed
    // only reads the route.
    queryColor() {
      const raw = this.$route?.query?.color;
      return typeof raw === "string" ? raw : "";
    },

    // A single reactive key so the watcher fires on any of slug / categorySlug
    // / productId change (e.g. clicking a related product without leaving the
    // ProductPage route component).
    routeKey() {
      return `${this.slug}::${this.categorySlug}::${this.productId}`;
    },

    // Every color the payload lists (selected + de-selected-but-imaged).
    allColors() {
      return Array.isArray(this.product?.colors) ? this.product.colors : [];
    },

    // Colors with at least one image to show. Since 2026-10-07 that is
    // nearly every color: the server fills a color without a chosen image
    // with its render or blank. Which colors carry the distributor's OWN
    // pick is the per-color `chosen` flag (see the `product` watcher).
    imagedColors() {
      return this.allColors.filter(
        (c) => Array.isArray(c.images) && c.images.length > 0
      );
    },

    // True when the product has any image at all. Drives the whole
    // real-image-vs-silhouette fork: when true we show real images; when
    // false we keep the legacy silhouette gallery.
    hasAnyImages() {
      return this.imagedColors.length > 0;
    },

    // The swatch set the buy box renders: EVERY color the payload lists.
    //
    // This deliberately reverses an earlier filter (Jake, 2026-08-26). Until
    // now, a product with ANY chosen image showed ONLY its imaged colors, on
    // the q7EQ3a0b reading that "chosen images are the source of truth". That
    // made the PDP disagree with the two other surfaces showing the same set:
    // the storefront product CARD and the product editor both list every
    // selected color. Product 128 (store `acmegolf`) has 8 selected colors and
    // 3 imaged ones, and the PDP rendered 3 dots while the card rendered 8.
    //
    // Jake's decision, verbatim: "Instead of a visual, show a message where
    // the image would be that says, 'No preview available for this color'."
    // So the missing preview is now surfaced in the GALLERY (see
    // showNoPreview) rather than by hiding the swatch. Chosen images remain
    // the source of truth for what the gallery SHOWS; they no longer decide
    // which colors a shopper may pick.
    //
    // `imagedColors` / `hasAnyImages` keep
    // their remaining consumer, `showNoPreview` -- the imaged-vs-silhouette
    // fork still turns on whether the product has any chosen image at all.
    // (The default-color heuristic does its OWN inline imaged-color scan in
    // the `product` watcher and never read these, so it is untouched here.)
    colors() {
      return this.allColors;
    },

    // True when the gallery should show "No preview available for this color"
    // in place of an image.
    //
    // NOW RARE (Jake, 2026-10-07): the server gives every color an image --
    // its chosen images, else its decorated render, else its supplier blank
    // (storefront_products.py _with_pdp_fallback_images). This message is
    // only for a color no tier can image at all. It no longer means "the
    // distributor chose nothing for this color", which the 2026-08-26
    // decision below assumed.
    //
    // SCOPED ON PURPOSE to the product that HAS chosen images but whose
    // SELECTED color does not. A product with ZERO imaged colors keeps the
    // legacy tinted-silhouette gallery untouched: that fallback is a real
    // rendering of the garment (front/back, tinted to the color) and is
    // strictly more informative than the message, so replacing it everywhere
    // would be a regression Jake did not ask for. The message exists for the
    // case where the shopper can SEE that other colors have photos and this
    // one does not -- there, a silhouette next to real photography reads as a
    // broken image rather than as a deliberate stand-in.
    showNoPreview() {
      return this.hasAnyImages && this.selectedColorImages.length === 0;
    },

    // The selected color's ordered chosen images ([] when none / silhouette
    // fallback). First = primary.
    selectedColorImages() {
      const found = this.allColors.find((c) => c.id === this.selectedColorId);
      return found && Array.isArray(found.images) ? found.images : [];
    },

    sizes() {
      // Order the (unordered) sizes by the canonical size list (Q4).
      return sortSizes(
        Array.isArray(this.product?.sizes) ? this.product.sizes : []
      );
    },

    /**
     * True when the product offers exactly ONE size, so the buy box drops
     * the size label and the per-size price and shows the quantity field
     * alone (Jake, 2026-09-07, live AdventHealth storefront).
     *
     * KEYED ON THE COUNT, deliberately not on the label being "OSFA".
     * The three live single-size AH products (Samsonite 102224, Brooks
     * Brothers BB18840, TravisMathew TMB205) all happen to say "OSFA"
     * today, but that string is the SUPPLIER's, not ours -- "One Size",
     * "OS" and "ONE SZ" all appear in this industry's feeds. Matching the
     * text would make the fix quietly stop working for the next supplier
     * without failing any test, whereas the count is what the rule is
     * actually about: there is no choice to present.
     *
     * Note this does NOT remove the price from the page. The product
     * price renders unconditionally in `sf-product-price` under the
     * product name, and the Add-to-cart button quotes the real total once
     * a quantity is entered; what goes away is only the duplicate.
     */
    isSingleSize() {
      return this.sizes.length === 1;
    },

    keyFeatures() {
      return Array.isArray(this.product?.key_features)
        ? this.product.key_features
        : [];
    },

    // The currently-selected color object; falls back to a neutral swatch so
    // the silhouette always has a tint even before a color loads.
    selectedColor() {
      const found = this.allColors.find((c) => c.id === this.selectedColorId);
      if (found) {
        return { name: found.name || "", hex: this.swatchHex(found) };
      }
      return { name: "", hex: SWATCH_FALLBACK };
    },

    // Logos eligible for the SELECTED color, straight off the PDP payload's
    // per-color map. Empty for a product with no placements, and for a color
    // whose placements all skip it -- the endpoint omits such colors rather
    // than mapping them to an empty list.
    eligibleLogos() {
      const map = this.product?.logos;
      if (!map || !this.selectedColorId) return [];
      const entries = map[this.selectedColorId];
      return Array.isArray(entries) ? entries : [];
    },

    // Shown whenever the color offers AT LEAST ONE logo; HIDDEN only at zero,
    // which would render an empty control.
    //
    // A single option is shown too, preselected (Jake, 2026-09-10: "Hiding
    // the picker is the wrong call. It should be shown with the single option
    // automatically selected"). The picker is not only a control, it is the
    // display of WHICH logo goes on the garment -- hiding it made the shopper
    // infer that from nothing. Live impact: 7 of the 34 AdventHealth styles
    // offer exactly one logo and showed no logo control at all.
    //
    // Jake, 2026-10-07 (a temporary workaround): whether a LONE logo is
    // shown is now the store's choice, `show_single_logo` on the PDP
    // payload, default off. A lone logo is not a shopper choice, so hiding
    // it loses no decision -- it is still preselected by
    // `reconcileLogoSelection` and sent on add-to-cart. Two or more logos
    // are a real choice and always show. The rule is `hidesLoneLogo`,
    // shared with the cart caption so the two surfaces decide alike.
    showLogoPicker() {
      const count = this.eligibleLogos.length;
      return count >= 1 && !hidesLoneLogo(count, this.product?.show_single_logo);
    },

    selectedLogo() {
      return (
        this.eligibleLogos.find((l) => l.id === this.selectedLogoId) || null
      );
    },

    // Base garment price. price_cents is whole cents; the display wants a
    // dollar amount. NO decoration bundling (base price only, per P6).
    // The product's "from" price in whole cents, or null when the server
    // sent none. `price_cents` is NULLABLE under two-mode pricing
    // (2026-09-29), and Number(null) is 0 -- reading it naively showed
    // "$0.00" for a product with no price.
    basePriceCents() {
      const raw = this.product ? this.product.price_cents : null;
      if (raw === null || raw === undefined) return null;
      const cents = Number(raw);
      return Number.isFinite(cents) && cents >= 0 ? cents : null;
    },

    // The formatted "from" price, or null (rendered as a placeholder).
    basePrice() {
      if (this.basePriceCents === null) return null;
      const formatted = formatCurrencyNumber(this.basePriceCents / 100);
      return formatted === "—" ? null : formatted;
    },

    pricePlaceholder() {
      return CURRENCY_EMPTY_PLACEHOLDER;
    },

    // The headline: the size prices through the shared "From" rule ("From
    // $X" when sizes differ, as on every card and table -- #3884 gate P2-6),
    // else the flat "from" price, else null (placeholder).
    headlinePrice() {
      const bySize = {};
      for (const size of this.sizes) {
        const cents = this.sizePriceCents(size);
        if (cents !== null) bySize[size.id] = cents;
      }
      const quoted = fromPriceLabel(bySize);
      if (quoted !== null) return quoted;
      return this.basePrice === null ? null : `$${this.basePrice}`;
    },

    // Every size is unpriced: nothing on this page can be ordered. Priced
    // sizes that are merely out in the SELECTED color do not count -- another
    // color may have them, so the product is still orderable.
    noSizeAvailable() {
      return (
        this.sizes.length > 0 &&
        this.sizes.every((size) => this.isSizePriceMissing(size))
      );
    },

    // The gallery frames. Chosen images are the source of truth (card
    // q7EQ3a0b): when the selected color has chosen images, the gallery is
    // exactly those real photos (first = primary), NO silhouette. Only when
    // the selected color has NO chosen image do we fall back to the tinted
    // silhouette front/back + the labeled striped placeholders (ADR 0061).
    galleryFrames() {
      const images = this.selectedColorImages;
      if (images.length > 0) {
        return images.map((url) => ({ type: "photo", url }));
      }

      // Imageless color on a product that HAS images: the gallery is the
      // no-preview message alone, so there are no frames. The thumbnail rail
      // renders per frame and therefore disappears with them -- there is
      // exactly one thing to look at, so a rail would either be an empty box
      // or a second copy of the same message.
      if (this.showNoPreview) {
        return [];
      }

      const secondView = CAP_SILHOUETTES.has(this.product?.silhouette)
        ? "side"
        : "back";
      const secondLabel = secondView === "side" ? "Side" : "Back";
      return [
        { type: "sil", view: "front", label: "Front" },
        { type: "sil", view: secondView, label: secondLabel },
        { type: "ph", label: "On-model" },
        { type: "ph", label: "Flat lay" },
        { type: "ph", label: "Logo detail" },
      ];
    },

    // `{}` (not undefined) when there are no frames at all -- the no-preview
    // branch renders instead, but the template still evaluates
    // `activeFrameObj.type` on the v-else-if chain.
    activeFrameObj() {
      return (
        this.galleryFrames[this.activeFrame] || this.galleryFrames[0] || {}
      );
    },

    totalQty() {
      return this.sizes.reduce(
        (sum, size) => sum + this.quantityFor(size.id),
        0
      );
    },

    // The SUM over selected sizes of qty x that size's OWN price.
    //
    // This was ``totalQty * basePriceCents`` -- one FLAT price
    // (``product.price_cents``) times the total count -- which is
    // structurally unable to be right the moment sizes differ in price. On
    // campaign product 128 a single 2XL quoted $14.98 against a real
    // $25.98 (Jake, live on 8083, 2026-08-28).
    //
    // No ``variant_prices`` lookup and no backend change are needed here:
    // this page's own payload already carries the per-size price. Verified
    // against GET /api/storefront/acmegolf/products/128, whose ``sizes``
    // are [{id:'XS', label:'XS', price_cents:1549}, ...
    // {id:'2XL', label:'2XL', price_cents:2598}].
    //
    // A size with no price is unavailable and counts 0 in ``quantityFor``,
    // so it never reaches this sum.
    //
    // With an exact quote for the current selection, each line is priced at
    // its quoted unit price instead (see `exactUnitBySize`).
    subtotalCents() {
      const exact = this.exactUnitBySize;
      return this.sizes.reduce((sum, size) => {
        const qty = this.quantityFor(size.id);
        if (!qty) {
          return sum;
        }
        const unit = exact ? exact[size.id] : this.sizePriceCents(size);
        return sum + qty * unit;
      }, 0);
    },

    subtotalFormatted() {
      const formatted = formatCurrencyNumber(this.subtotalCents / 100);
      return formatted === "—" ? "0.00" : formatted;
    },

    // The selection as quote lines, `[{ size, quantity }]`, in size order.
    quoteLines() {
      return this.sizes
        .map((size) => ({ size: size.id, quantity: this.quantityFor(size.id) }))
        .filter((line) => line.quantity > 0);
    },

    // What the quote is FOR, or null when there is nothing to ask: no
    // selection, no colour, or a Fixed price (exact already, no breaks).
    // Any other mode -- including a payload that does not say -- is quoted,
    // the same safe side `subtotalIsEstimate` takes.
    quoteKey() {
      if (!this.product || this.product.pricing_mode === "fixed") return null;
      if (this.selectedColorId == null || this.quoteLines.length === 0) {
        return null;
      }
      return JSON.stringify([
        this.slug,
        this.productId,
        this.selectedColorId,
        this.quoteLines,
      ]);
    },

    // `{ [size]: unitCents }` for the CURRENT selection when its quote came
    // back with every line priced; null otherwise (pending, failed, a line
    // with no price, or a quote for an earlier selection).
    exactUnitBySize() {
      if (this.quoteKey === null || !this.quote) return null;
      if (this.quote.key !== this.quoteKey) return null;
      return this.quote.unitBySize;
    },

    // A quote for the current selection is still on its way.
    quotePending() {
      return (
        this.quoteKey !== null &&
        (!this.quote || this.quote.key !== this.quoteKey)
      );
    },

    // True when the total above is NOT the charge. `sizes[].price_cents` is
    // `price_for` at quantity 1. A Fixed price has no quantity breaks, so
    // its total is exact; Store pricing steps down with the LINE quantity,
    // so it is exact only once the quote for this selection is back with
    // every line priced. Any mode other than "fixed" -- including a payload
    // that does not say -- is otherwise an estimate: an unlabeled estimate
    // is exactly the second price source this replaces (#3884 gate P1-1).
    subtotalIsEstimate() {
      return (
        this.totalQty > 0 &&
        this.product?.pricing_mode !== "fixed" &&
        this.exactUnitBySize === null
      );
    },

    // The "estimated at 1-unit prices" note explains a SETTLED estimate (the
    // quote failed or a line has no price). While a quote is in flight the
    // "est." on the total says enough; the note would flash on every
    // keystroke.
    showEstimateNote() {
      return this.subtotalIsEstimate && !this.quotePending;
    },

    // The total as shown on the button and the mobile bar.
    totalText() {
      const est = this.subtotalIsEstimate ? "est. " : "";
      return `${est}$${this.subtotalFormatted}`;
    },

    // The button shows "Add to cart - N for <total>".
    labelShowsTotal() {
      return (
        !this.isPreview &&
        !this.addedToCart &&
        !this.needsLogoChoice &&
        this.totalQty > 0
      );
    },

    // The store-editor preview embeds this page in a same-origin iframe
    // with `?preview=1`. In that mode the session gate SKIPS the shopper
    // token swap, so `$store.state.account` is the EDITOR's account (an
    // impersonated distributor), not a shopper -- and `onAddToCart` posts
    // to `/accounts/<that id>/update_cart_quantity`. Adding to cart from
    // the preview would therefore write real cart rows onto the
    // distributor's own account. Browsing the preview is fine; cart
    // WRITES are not. See router/storefrontPreview.js.
    isPreview() {
      return isStorefrontPreview(this.$route);
    },

    addToCartLabel() {
      if (this.isPreview) {
        return "Add to cart - disabled in preview";
      }
      if (this.addedToCart) {
        // The button IS the confirmation, so the label must also say what the
        // button now DOES -- "Added" alone would leave a control whose action
        // silently changed from adding to navigating.
        return "Added - Go to Cart";
      }
      if (this.needsLogoChoice) {
        // Same precedent as the preview label above: a disabled control
        // states its own reason. A dead button with an unchanged "Add to
        // cart" label reads as breakage, and the shopper has no way to learn
        // that the picker directly above it is what unblocks them.
        return "Select a logo to continue";
      }
      if (this.totalQty > 0) {
        // "N for $TOTAL", not "N @ $TOTAL" (Jake, 2026-08-28): "the at
        // symbol implies per unit price as opposed to total price of the
        // selection". The amount has always been a TOTAL, so on any
        // mixed-size selection "@" asserted a per-unit price that was
        // wrong for most of the units it counted. "for" is honest for any
        // mix, so one label shape covers every case.
        return `Add to cart - ${this.totalQty} for ${this.totalText}`;
      }
      return "Add to cart";
    },

    // A logo choice is OUTSTANDING when the color offers a real choice (more
    // than one eligible logo) and the shopper has not made it.
    //
    // WHY THIS GATES THE CART. `order_items.resolved_artwork_id` is validated
    // server-side against the eligible set, but a line carrying no
    // `selected_artwork_id` falls back to the server's own
    // first-placement-wins resolution -- so an unchosen add silently records
    // a logo the shopper never picked, surfacing only on the offline export,
    // on a garment, at the event. Jake, 2026-09-10: "for items with multiple
    // logo choices, we shouldn't allow someone to add to cart without first
    // making a logo choice."
    //
    // Deliberately scoped to >1: exactly one option is auto-selected by
    // `reconcileLogoSelection` and already satisfies this, and ZERO options
    // must NOT block -- there is nothing to choose, and blocking would make
    // an undecorated product unbuyable.
    needsLogoChoice() {
      return this.eligibleLogos.length > 1 && this.selectedLogoId == null;
    },

    canAddToCart() {
      if (this.isPreview) return false;
      if (this.noSizeAvailable) return false;
      if (this.needsLogoChoice) return false;
      return this.totalQty > 0;
    },

    // Home / Category / Product. Home + Category link; the terminal product
    // crumb is plain text. Capped at 3 by the breadcrumb component.
    //
    // The category crumb is pushed only when a category LINK is actually
    // possible -- i.e. when storefrontCategoryPath returns a path. Jake,
    // 2026-08-26: "If a slug is empty there should be no link. Those would be
    // broken links anyway."
    //
    // This guard used to test the category NAME while building the path from
    // the SLUG, which is what shipped the reported bug: the HOME category is
    // named "Home" but its slug is "" BY DESIGN (store_category.py sets
    // slug = "" for the is_home row). The name passed, the empty slug
    // rendered /shop/<store>/c/ -- a path matching no route -- and the label
    // duplicated the "Home" crumb already at index 0, giving the
    // "Home > Home > Product" trail Jake and Mike saw. Guard and URL now read
    // the SAME value, which is the property that makes this class of bug
    // impossible rather than merely fixed.
    breadcrumbTrail() {
      const trail = [{ label: "Home", path: `/shop/${this.slug}` }];
      const catPath = storefrontCategoryPath(this.slug, this.categorySlug);
      const catName = this.relatedCategory?.name;
      if (catPath && catName) {
        trail.push({ label: catName, path: catPath });
      }
      if (this.product?.name) {
        trail.push({ label: this.product.name });
      }
      return trail;
    },

    // "Keep Shopping" -- where the shopper came FROM.
    //
    // Derived from `breadcrumbTrail` rather than rebuilt: the trail already
    // answers "how did you get here", and it already refuses to link an
    // unlinkable category (`storefrontCategoryPath` returns null for the Home
    // category's empty slug, which is a designed value, not a defect). A
    // second hand-rolled "where did I come from" mechanism here is exactly the
    // N+1th call site that reintroduces the /shop/<store>/c/ dead link --
    // Jake's own reported URL was /shop/acmegolf/c//p/128.
    //
    // The trail's LAST LINKED crumb is the answer: the category when one is
    // linkable, the store home otherwise (crumb 0 is always Home). The
    // terminal product crumb carries no path, so it can never be picked.
    keepShoppingTarget() {
      const linked = this.breadcrumbTrail.filter((crumb) => crumb.path);
      const last = linked[linked.length - 1];
      // `/shop/${slug}` rather than "/" as the floor: a storefront visitor
      // belongs in their store, and crumb 0 always supplies it in practice.
      return (last && last.path) || `/shop/${this.slug}`;
    },

    relatedCategoryName() {
      return this.relatedCategory?.name || "this category";
    },

    // <=4 OTHER products from the same category, in sort_order, excluding the
    // current product. Not a rec engine -- just the category's own ordering.
    relatedProducts() {
      const list = Array.isArray(this.relatedCategory?.products)
        ? this.relatedCategory.products
        : [];
      return list
        .filter((p) => String(p.id) !== String(this.productId))
        .slice(0, RAIL_CAP);
    },
  },

  watch: {
    // Re-quote the selection, debounced, whenever it changes. A key of null
    // (nothing to ask) just cancels any pending request.
    quoteKey(key) {
      this.cancelQuote();
      if (key === null) return;
      this.quoteTimer = setTimeout(() => {
        this.quoteTimer = null;
        this.requestQuote(key);
      }, QUOTE_DEBOUNCE_MS);
    },

    routeKey: {
      immediate: true,
      handler() {
        if (!this.slug) return;
        this.loadAll();
      },
    },

    // When the product payload arrives (or changes), reset the buy-box state.
    //
    // COLOR PRECEDENCE, most specific wins:
    //
    //   1. ?color= deep link  -- an explicit, per-visit request for ONE color.
    //   2. default_color      -- the builder's store-wide default pick
    //                            (campaign_products.default_image).
    //   3. lead_color         -- the color of the first chosen image in the
    //                            editor's global order: the card thumbnail
    //                            (null for data saved without positions).
    //   4. first CHOSEN color -- per-color `chosen` flag; pre-existing
    //                            heuristic, kept when the fallback landed.
    //   5. first listed color -- no color has a chosen image.
    //
    // The deep link MUST outrank the default: Jake shipped the QR-code flow
    // (https://trello.com/c/PWUeRl0u) so a PRINTED code opens a specific
    // color. A store default that silently overrode it would break a physical
    // artifact already in the world, which cannot be re-printed.
    //
    // Both the deep link and the default match through the SAME
    // normalizeColorKey, so they can never disagree about what "Black/ Black"
    // means; the canonical color.id is always what gets stored, never the
    // normalized or payload-supplied form.
    //
    // Every step degrades to the next: a default naming a color this product
    // no longer carries (stale after the builder removed it) simply matches
    // nothing and falls through to the heuristic -- today's exact behavior,
    // no empty gallery, no missing swatch. Same for a payload that omits
    // default_color entirely.
    //
    // Clear the matrix and reset the gallery to the primary frame either way.
    product: {
      handler(next) {
        const colors =
          next && Array.isArray(next.colors) ? next.colors : [];

        // Resolve a requested color name against this product's colors,
        // returning the CANONICAL entry (or null when it names nothing here).
        const matchColor = (wanted) => {
          if (!wanted) return null;
          const key = normalizeColorKey(wanted);
          if (!key) return null;
          return colors.find((c) => normalizeColorKey(c.id) === key) || null;
        };

        // 1. Deep link, then 2. the store default, then 3. the LEAD color --
        // the color of the first chosen image in the editor's global
        // cross-color order, i.e. the image the storefront card shows
        // (#4154 gate P1-1; null for data saved without positions). Note
        // this is an explicit ordered fallthrough, NOT a merge -- each step
        // is only consulted when the previous one resolved nothing.
        const chosen =
          matchColor(this.queryColor) ||
          matchColor(next && next.default_color) ||
          matchColor(next && next.lead_color);

        if (chosen) {
          this.selectedColorId = chosen.id;
        } else {
          // 4. Prefer the first color with a CHOSEN image -- the one the
          // storefront card shows -- and only fall back to 5. the first
          // listed color when no color is chosen. UNCHANGED by the 2026-10-07
          // per-color fallback: the server now fills every color with a
          // render or blank, so "has images" no longer means "chosen"; the
          // per-color `chosen` flag keeps this step's meaning (#4144 gate
          // P1-1: a shopper who clicked the card's White photo must land on
          // White, not on a Lime blank). A payload without the flag (an
          // older API during a deploy) falls back to "has images".
          const isChosen = (c) =>
            c.chosen === true ||
            (c.chosen === undefined &&
              Array.isArray(c.images) &&
              c.images.length > 0);
          const firstChosen = colors.find(isChosen);
          if (firstChosen) {
            this.selectedColorId = firstChosen.id;
          } else if (colors.length > 0) {
            this.selectedColorId = colors[0].id;
          } else {
            this.selectedColorId = null;
          }
        }
        // Seed the logo choice for the resolved color: preselects when the
        // color offers exactly one logo, clears when it offers none. Runs
        // AFTER selectedColorId settles, since eligibility is per-color.
        this.selectedLogoId = null;
        this.reconcileLogoSelection();
        // A ONE-size product opens with 1 already in the field; every other
        // product opens with an empty matrix. See `initialQuantities`.
        //
        // Reads the watcher's own `next` rather than `this.sizes`, so the
        // seed cannot depend on whether the computed has already recomputed
        // against the new payload at this point in the flush.
        this.quantities = this.initialQuantities(next && next.sizes);
        this.activeFrame = 0;
        // A new product (or a re-navigation to this route) starts idle. The
        // state is deliberately NOT persisted across a reload: it reports the
        // outcome of an add THIS shopper just performed on THIS page, and a
        // restored "Added" over a refetched, empty matrix would be a claim the
        // page has no evidence for.
        this.addedToCart = false;
      },
    },
  },

  mounted() {
    this.subscribeToBreakpoint();
  },

  // A media-query listener that outlives the component writes into a dead
  // instance on the next rotation.
  beforeUnmount() {
    this.unsubscribeFromBreakpoint();
    this.cancelQuote();
  },

  methods: {
    /** Drop a scheduled quote and orphan any in flight. */
    cancelQuote() {
      if (this.quoteTimer !== null) {
        clearTimeout(this.quoteTimer);
        this.quoteTimer = null;
      }
      this.quoteSeq += 1;
    },

    /**
     * Ask for the selection `key` names and settle `quote` for it.
     *
     * Only the LATEST request may settle (`quoteSeq`), and `exactUnitBySize`
     * reads it only while its key is still the current one, so a slow answer
     * for "12 of S" can never price a later "6 of S". A failure settles as
     * "no exact total" and the page keeps its labelled estimate.
     */
    async requestQuote(key) {
      const seq = (this.quoteSeq += 1);
      const lines = this.quoteLines;
      let unitBySize = null;
      try {
        unitBySize = await fetchPdpQuote(
          this.$axios,
          this.slug,
          this.productId,
          this.selectedColorId,
          lines
        );
      } catch (err) {
        unitBySize = null;
      }
      if (seq !== this.quoteSeq) return;
      this.quote = { key, unitBySize };
    },

    /**
     * Track the mobile breakpoint.
     *
     * `window.matchMedia` is absent in jsdom and in any SSR-ish context, so
     * its absence must degrade to "desktop" rather than throw -- the
     * desktop grid is the safe default, being what shipped.
     */
    subscribeToBreakpoint() {
      if (typeof window === "undefined" || !window.matchMedia) return;
      const mq = window.matchMedia(MOBILE_MEDIA_QUERY);
      this.mobileMediaQuery = mq;
      this.isMobile = mq.matches;
      this.onBreakpointChange = (event) => {
        this.isMobile = event.matches;
      };
      if (mq.addEventListener) {
        mq.addEventListener("change", this.onBreakpointChange);
      } else if (mq.addListener) {
        // Safari < 14 predates the EventTarget API on MediaQueryList.
        mq.addListener(this.onBreakpointChange);
      }
    },

    unsubscribeFromBreakpoint() {
      const mq = this.mobileMediaQuery;
      if (!mq || !this.onBreakpointChange) return;
      if (mq.removeEventListener) {
        mq.removeEventListener("change", this.onBreakpointChange);
      } else if (mq.removeListener) {
        mq.removeListener(this.onBreakpointChange);
      }
      this.mobileMediaQuery = null;
      this.onBreakpointChange = null;
    },

    /**
     * Write a size's quantity from the shared stepper.
     *
     * `null` means the size LEAVES the order (decision 9, "zero means
     * absent"): a stored 0 would ride into the cart payload as a real
     * line. This mirrors what `onQtyInput` already does for an emptied
     * field, so both entry paths agree.
     */
    onStepperQty(sizeId, value) {
      const next = { ...this.quantities };
      if (value == null || Number(value) <= 0) {
        delete next[sizeId];
      } else {
        next[sizeId] = Math.floor(Number(value));
      }
      this.quantities = next;
      this.clearAddedState();
    },

    swatchHex(color) {
      return color && color.hex ? color.hex : SWATCH_FALLBACK;
    },

    // Keep the logo choice coherent with the color. Called on load and on
    // every color change: a logo eligible for Navy may not be eligible for
    // White, and carrying a stale selection would submit a logo the shopper
    // can no longer see offered.
    reconcileLogoSelection() {
      const eligible = this.eligibleLogos;
      if (eligible.length === 0) {
        this.selectedLogoId = null;
        return;
      }
      if (eligible.length === 1) {
        // Exactly one option: preselect it. The picker RENDERS it as the
        // selected option, and the choice rides the cart so the order records
        // WHICH logo was applied rather than leaving the server to re-derive
        // it. This preselection is also what keeps `needsLogoChoice` false
        // for a single-logo color -- there is no choice left to make.
        this.selectedLogoId = eligible[0].id;
        return;
      }
      const stillEligible = eligible.some((l) => l.id === this.selectedLogoId);
      if (!stillEligible) this.selectedLogoId = null;
    },

    // Tile tint follows the ASSET's substrate, falling back to neutral when
    // the payload could not derive one -- never guess a tint, since a wrong
    // one can render light-on-dark art invisible.
    logoTileClass(logo) {
      if (logo && logo.substrate === "dark") return "is-dark";
      if (logo && logo.substrate === "light") return "is-light";
      return "is-neutral";
    },

    selectLogo(logo) {
      this.selectedLogoId = logo ? logo.id : null;
      // A different logo is a different thing to add; the "Added" state
      // would otherwise assert that THIS logo is already in the cart.
      this.clearAddedState();
    },

    selectColor(color) {
      this.selectedColorId = color.id;
      // Re-filter the logo options for the new color, dropping a selection
      // that is no longer offered.
      this.reconcileLogoSelection();
      // A different colorway is a different cart line. Carrying "Added" across
      // the switch would assert that THIS color is in the cart when it is not.
      this.clearAddedState();
      // Reset the gallery to the primary frame so switching color always lands
      // on that color's first image (or the front silhouette in fallback).
      this.activeFrame = 0;
      // Keep the URL in sync so "copy the URL I'm looking at" shares the
      // color on screen (Boulders: Storefront color deep-link + QR share
      // code, https://trello.com/c/PWUeRl0u). router.replace, NEVER push --
      // a push per swatch click would make Back walk through every color the
      // shopper tried instead of leaving the page.
      //
      // Pass the concrete `path`, never a query-only location: without a path
      // Vue Router rebuilds the url from the current name + params, and its
      // stringifier rejects the HOME category's EMPTY `categorySlug` (the
      // route regex parses `/c//p/<id>` but does not make the param
      // optional), throwing `Missing required param "categorySlug"` so the
      // replace never runs. Same mechanism as router/storefrontPreview.js.
      this.$router.replace({
        path: this.$route.path,
        query: { ...this.$route.query, color: color.id },
      });
    },

    /**
     * The `quantities` map a freshly loaded (or freshly cleared) matrix
     * starts from.
     *
     * Empty for every product with a real size range -- those blanks ARE
     * the choice the shopper has not made yet, and pre-filling any of them
     * would put garments in a cart nobody asked for.
     *
     * A ONE-size product instead starts at 1 (Jake, 2026-09-22): "the
     * default quantity for the OSFA size should be 1 instead of 0". With a
     * single size there is nothing to choose among -- opening the page IS
     * the decision about what, leaving only how many -- so the smallest
     * meaningful answer is a better start than a blank the shopper must
     * fill to buy the one thing this page sells.
     *
     * KEYED ON THE COUNT via `isSingleSize`, never on the label being
     * "OSFA": that string is the supplier's ("One Size", "OS", "ONE SZ"
     * all appear in this industry's feeds), so matching it would make the
     * default quietly stop working for the next supplier without failing a
     * test. See `isSingleSize` for the longer version of that argument.
     *
     * SEEDED, NOT PINNED. This is the starting value of an ordinary field:
     * the shopper can step up, step down to 0, or clear it, and
     * `onQtyInput` / `onStepperQty` still DELETE the key on an emptied
     * field so it stays empty. There is deliberately no minimum of 1 and
     * no cart-aware "only default when the cart is empty" -- both would add
     * a state this page does not otherwise have. Add-to-cart remains gated
     * on quantity > 0.
     *
     * Takes the size list as an argument rather than reading `this.sizes`
     * so the caller in the `product` watcher can pass the incoming
     * payload's sizes directly.
     */
    initialQuantities(sizes) {
      const list = Array.isArray(sizes) ? sizes : [];
      if (list.length !== 1) {
        return {};
      }
      const only = list[0];
      // A payload can carry a size with no id (older/partial rows). Keying
      // a quantity on `undefined` would write a line the cart cannot
      // resolve, so fall back to the empty matrix -- today's behavior.
      return only && only.id != null ? { [only.id]: 1 } : {};
    },

    // An unavailable (unpriced) size always counts 0 -- so it is never in
    // the subtotal, the total, or the add-to-cart payload, even if a value
    // reached its (disabled) input.
    quantityFor(sizeId) {
      const size = this.sizes.find((s) => s.id === sizeId);
      if (size && this.isSizeUnavailable(size)) return 0;
      const q = Number(this.quantities[sizeId]);
      return Number.isFinite(q) && q > 0 ? Math.floor(q) : 0;
    },

    /**
     * This size's own price in whole cents, in the SELECTED color, or null
     * when it has none.
     *
     * Decoration is priced per color (Jake, 2026-10-22): a placement split
     * to light garments only is not charged on a dark garment. So the
     * selected swatch's entry in ``size_prices_by_color`` is the price; a
     * size missing from it has no price in that color. A payload without
     * the map, or without this color, falls back to ``sizes[].price_cents``
     * (the preselected color's price). Both are shipped by
     * ``GET /api/storefront/<slug>/products/<id>``; the flat
     * ``product.price_cents`` is a "from" price and is wrong for any
     * upcharged size, so it is never borrowed (#3884 gate P1-1). A null, an
     * absent key, or a non-number all mean "no price", and Number(null) is
     * 0, so a null is checked before the conversion.
     */
    sizePriceCents(size) {
      if (!size) return null;
      const byColor = this.product ? this.product.size_prices_by_color : null;
      const colorPrices =
        byColor && this.selectedColorId != null
          ? byColor[this.selectedColorId]
          : null;
      const raw = colorPrices ? colorPrices[size.id] : size.price_cents;
      if (raw === null || raw === undefined) return null;
      const cents = Number(raw);
      return Number.isFinite(cents) && cents >= 0 ? cents : null;
    },

    /**
     * True when the server has NO price for this size (2026-09-29): its
     * `price_cents` is null (a Fixed-price size with no price yet, or a
     * pricing failure) or missing. An unavailable size shows no price,
     * cannot be selected, and is never counted.
     */
    isSizePriceMissing(size) {
      return this.sizePriceCents(size) === null;
    },

    /**
     * The background hint's level for this size in the SELECTED color:
     * `null` (plenty), an integer (low stock; 0 = not available), or
     * `undefined` when the hint says nothing -- no answer yet, a failed
     * call, an unknown color or size. `undefined` never disables anything
     * (fail open, Jake 2026-10-26).
     */
    sizeColorLevel(size) {
      const byColor = this.sizeAvailability;
      if (!size || !byColor || this.selectedColorId == null) return undefined;
      if (!Object.prototype.hasOwnProperty.call(byColor, this.selectedColorId)) {
        return undefined;
      }
      const sizes = byColor[this.selectedColorId];
      return Object.prototype.hasOwnProperty.call(sizes, size.id)
        ? sizes[size.id]
        : undefined;
    },

    /**
     * True when a size cannot be ordered: either the server has no price for
     * it (above), or the supplier has none in the selected color (the
     * hint). Both disable the control and count 0; only the unpriced case
     * can make the whole product unorderable (`noSizeAvailable`).
     */
    isSizeUnavailable(size) {
      return this.isSizePriceMissing(size) || this.sizeColorLevel(size) === 0;
    },

    /**
     * Tooltip text for a size's chip and quantity control, or "" for none:
     * "Not available in {color}" when it is out in the selected color,
     * "Only {n} available in {color}" when low. An unpriced size keeps its
     * own "Not available" marker and gets no color-specific text.
     */
    sizeAvailabilityTip(size) {
      if (this.isSizePriceMissing(size)) return "";
      const level = this.sizeColorLevel(size);
      const color = this.selectedColor ? this.selectedColor.name : "";
      if (level === undefined || level === null || !color) return "";
      if (level === 0) return `Not available in ${color}`;
      return `Only ${level} available in ${color}`;
    },

    /**
     * This size's price as a display string, or "" when it has none.
     *
     * Empty rather than "$0.00" or "$--": a size with no price shows NO
     * price line at all. Quoting a fabricated zero under a size the
     * shopper can add to their cart is worse than showing nothing.
     */
    sizePriceLabel(size) {
      // The selected color's price (see `sizePriceCents`); null is "no
      // price" (2026-09-29), never a real $0.00.
      const cents = this.sizePriceCents(size);
      if (cents === null) return "";
      const formatted = formatCurrencyNumber(cents / 100);
      return formatted === "\u2014" ? "" : `$${formatted}`;
    },

    onQtyInput(sizeId, event) {
      const raw = event && event.target ? event.target.value : "";
      if (raw === "" || raw == null) {
        // Preserve the empty field (so the placeholder shows) rather than
        // forcing a 0 the shopper must delete.
        const next = { ...this.quantities };
        delete next[sizeId];
        this.quantities = next;
        return;
      }
      const parsed = Math.floor(Number(raw));
      const clean = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
      this.quantities = { ...this.quantities, [sizeId]: clean };
      this.clearAddedState();
    },

    // Per-color photo override (Q3): only when the payload clearly maps a color
    // to an image. hero_image_url is NOT reliably per-color-keyed, so beta
    // ships tinted silhouettes for the front/back frames and never a photo
    // override here. The frame hook stays so a later per-color image slot
    // wires in without touching the template.
    framePhotoUrl() {
      return null;
    },

    loadAll() {
      this.fetchChrome(this.slug);
      this.fetchProduct(this.slug, this.productId);
      // Background hint (never awaited, never blocks the page or add-to-cart):
      // which sizes the supplier cannot supply in the selected color.
      this.fetchAvailability(this.slug, this.productId);
      // The rail + the category-name breadcrumb crumb both come from the
      // category endpoint (products already in sort_order).
      this.fetchRelatedCategory(this.slug, this.categorySlug);
    },

    // The single control has two actions. Which one runs is decided HERE, not
    // by two competing @click bindings on conditionally-rendered buttons --
    // one button, one handler, one place to read the behavior (one mechanism
    // per concept, docs/design-philosophy.md).
    onAddButtonClick() {
      if (this.addedToCart) {
        this.$router.push(`/shop/${this.slug}/cart`);
        return;
      }
      return this.onAddToCart();
    },

    // Anything that gives the shopper something NEW to add retires the added
    // state. Leaving the button on "Added - Go to Cart" while a fresh
    // quantity sits in the matrix would make the next click discard that
    // quantity and navigate away -- the button would be lying about both what
    // is in the cart and what it is about to do.
    clearAddedState() {
      this.addedToCart = false;
    },

    async onAddToCart() {
      if (!this.canAddToCart || this.addingToCart) return;

      // A genuine shopper resolves to their swapped-in shopper account; a
      // privileged session (back-office user / impersonating SA) resolves to
      // the actor's own account. Both write a real cart row -- the SA's cart
      // is the SA's own, and survives stopping impersonation ("it's your
      // cart", Jake 2026-08-26).
      const accountId = resolveCartAccountId({
        vuexStore: this.$store,
        piniaAccountId: this.accountStore?.accountId ?? null,
      });
      if (accountId == null) {
        // Neither a shopper session NOR any actor signal. Genuinely
        // sessionless -- keep telling the user rather than inventing an id.
        this.$store.dispatch(
          "raiseError",
          "We couldn't find your session to add items to your cart."
        );
        return;
      }

      const product = this.product;
      const campaignProductId = product.campaign_product_id ?? product.id;
      const primaryValue = this.selectedColorId;

      // One RPC per size with qty > 0 (bulk B2B add). The cart keys line items
      // per (campaign_product_id, color, size), so each call adds/updates a
      // DISTINCT line. update_cart_quantity is ABSOLUTE, so merge with any
      // pre-existing line for the same variant before writing (so a second
      // "add 2 of M" does not clobber an earlier M line to 2).
      const rows = this.sizes
        .map((size) => ({ sizeId: size.id, qty: this.quantityFor(size.id) }))
        .filter((r) => r.qty > 0);

      if (rows.length === 0) return;

      this.addingToCart = true;
      try {
        // Sequential (not parallel): update_cart_quantity reads-modifies-writes
        // the store's cart, so concurrent calls would lose writes. Each call
        // returns the full updated cart; commit the last one.
        //
        // The merge base is THIS store's cart as the server holds it now
        // (`store_cart`, per-store carts U6) -- the account carries no cart,
        // and a cart read for another store would merge the wrong lines. A
        // failed read fails the add: an absolute write on a guessed base
        // could overwrite a line the shopper already has.
        let lastCart = await fetchStoreCart(
          this.$axios,
          accountId,
          this.gateStoreId
        );
        for (const row of rows) {
          const secondaryValue = row.sizeId;
          let quantity = row.qty;

          const cart = lastCart;
          // The merge base is the line with the SAME logo: a different logo
          // on this color + size is a different line, and summing its
          // quantity in would start the new line at the wrong count.
          if (Array.isArray(cart)) {
            const existing = cart.find(
              (item) =>
                String(item.campaign_product_id) ===
                  String(campaignProductId) &&
                item.primary_attribute_value === primaryValue &&
                item.secondary_attribute_value === secondaryValue &&
                artworkKey(item.selected_artwork_id) ===
                  artworkKey(this.selectedLogoId)
            );
            if (existing) {
              quantity += existing.quantity || 0;
            }
          }

          const response = await this.$axios.post(
            `/accounts/${accountId}/update_cart_quantity`,
            {
              meta: {
                method: "update_cart_quantity",
                args: {
                  campaign_product_id: campaignProductId,
                  primary_attribute_value: primaryValue,
                  secondary_attribute_value: secondaryValue,
                  quantity,
                  prevent_cart_update_on_error: true,
                  ...cartStoreArgs(this.gateStoreId),
                  // The shopper's logo choice. Omitted (not null) when the
                  // product offers none, so legacy/undecorated lines keep
                  // the exact payload shape they had before the picker.
                  ...(this.selectedLogoId != null
                    ? { selected_artwork_id: this.selectedLogoId }
                    : {}),
                },
              },
            }
          );
          lastCart = response.data.meta.result.cart;
        }

        // Mirror the server's authoritative cart for this store into Vuex
        // (`state.cart`) so the topbar badge and the cart page see it. It
        // needs no account object, so a privileged session commits too.
        this.$store.commit("setCart", lastCart);
        // NO success snackbar (Jake, 2026-08-29): it covered the cart icon
        // and the profile menu. The confirmation is the button itself, which
        // is both less obtrusive and closer to the thing the shopper just
        // acted on. Errors still toast -- see the catch below -- because an
        // error must be readable after the control it came from has moved on.
        //
        // Set ONLY on the success path, and after the RPC has resolved: a
        // rejected add falls through to the catch and leaves this false, so
        // the button cannot go green over a cart that was never written.
        this.addedToCart = true;
        // Clear the matrix after a successful add so a second add starts fresh.
        //
        // A single-size product re-seeds its 1 rather than going blank. The
        // page is still showing exactly one size, so the reasoning that puts
        // a 1 there on load has not changed -- and a field that empties
        // itself the instant a shopper adds one reads as the add having
        // failed, which is the opposite of what just happened. The button
        // still reports success independently ("Added - Go to Cart"), so
        // this is a second coherent signal, not the only one.
        this.quantities = this.initialQuantities(this.product && this.product.sizes);
      } catch (err) {
        this.$store.dispatch("raiseError", err);
      } finally {
        this.addingToCart = false;
      }
    },
  },
};
</script>

<style scoped>
/* Sticky footer (Jake, 2026-08-26): the legal-links footer sits at the
   BOTTOM OF THE VIEWPORT when the page content is shorter than the
   viewport, and below the content when it is longer.

   Deliberately `min-height: 100%` and NOT `100dvh`. StorefrontLayout is a
   fixed-viewport shell (`.storefront-layout` is `height: 100dvh;
   overflow: hidden`) whose `.storefront-main` is the scroll container
   (`flex: 1; overflow-y: auto`). Declaring a viewport height here would
   stack a SECOND full viewport inside that scroller and force a scrollbar
   on a short page -- the opposite of the ask. Filling the scroll
   container's height is exactly enough.

   `margin-top: auto` on the footer (the flex column's last child) then
   consumes the free space above it. On a long page there is no free space,
   so the footer simply follows the content. No `position: fixed` -- that
   would overlay content on long pages. */
.sf-product {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.sf-product > .sf-footer {
  margin-top: auto;
}

.sf-product__loading,
.sf-product__error {
  padding: 40px 32px;
  font-size: 15px;
  color: var(--tf-fg-3, #616161);
}

/* Item 1: .sf-product__body owns the width cap + side padding (it nests
   inside the shared .sf-container); .sf-product__grid keeps only its own
   vertical padding so it doesn't double up against the container's. */
.sf-product__body {
  padding: 18px 32px 32px;
}

.sf-product__grid {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 44px;
  align-items: start;
}

.sf-product__gallery {
  display: flex;
  gap: 14px;
  position: sticky;
  top: 108px;
}

.sf-product__thumbs {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sf-product__thumb {
  width: 62px;
  height: 62px;
  border-radius: 8px;
  overflow: hidden;
  border: 1.5px solid var(--tf-border, #e0e0e0);
  background: #fff;
  padding: 0;
  cursor: pointer;
}

.sf-product__thumb.is-active {
  border-color: var(--tf-primary, #119ae9);
}

.sf-product__main {
  flex: 1;
  position: relative;
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 14px;
  overflow: hidden;
  aspect-ratio: 1;
  /* White for the same reason as the grid card's media box: this surface
   * letterboxes the SAME white-background photos, so a tint shows as bars
   * either side of the garment. Leaving this one tinted while whitening the
   * card would put a visible seam between a product card and the page it
   * opens. */
  background: #fff;
}

.sf-product__thumb-photo {
  width: 100%;
  height: 100%;
  /* `contain`, NOT `cover`.
   *
   * The thumb box is a fixed square and the garment photos are not square,
   * so `cover` filled the box by CROPPING the overflow -- the reported bug:
   * thumbnails lost the top and bottom of the garment, which is precisely
   * the part a shopper is scanning for. `contain` fits the whole photo and
   * letterboxes the remainder against the thumb's white background.
   *
   * The box keeps its fixed square footprint (see .sf-product__thumb), so
   * letterboxing happens INSIDE the thumb and the rail stays aligned rather
   * than going ragged with each photo's aspect ratio. */
  object-fit: contain;
  display: block;
}

.sf-product__main-photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.sf-product__ph {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: repeating-linear-gradient(
    135deg,
    #eef3f7 0 12px,
    #e5edf3 12px 24px
  );
  color: #8aa0b0;
  font-size: 12px;
}

.sf-product__no-preview {
  position: absolute;
  inset: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  font-size: 14px;
  color: var(--tf-fg-3, #616161);
  background: var(--tf-surface-light, #f5f8fa);
}

.sf-product__ph--tiny {
  font-size: 8px;
  padding: 2px;
}

.sf-product__buybox {
  font-size: 14px;
  color: var(--tf-fg-1, #212121);
}

.sf-product__style {
  font-size: 12px;
  color: var(--tf-fg-3, #616161);
  font-family: var(--tf-font-mono, monospace);
  margin-bottom: 8px;
}

.sf-product__name {
  font-weight: 700;
  font-size: 30px;
  line-height: 1.12;
  letter-spacing: -0.5px;
  margin: 0 0 14px;
}

.sf-product__price {
  font-size: 22px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  margin-bottom: 18px;
}

.sf-product__colors {
  margin-bottom: 18px;
}

.sf-product__colors-label {
  font-size: 13px;
  margin-bottom: 10px;
}

.sf-product__colors-label span {
  color: var(--tf-fg-3, #616161);
  margin-left: 4px;
}

.sf-product__swatches {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.sf-product__swatch {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  padding: 0;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.14);
}

.sf-product__swatch.is-selected {
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--tf-primary, #119ae9);
}

.sf-product__logos {
  margin-top: 1.25rem;
}

.sf-product__logos-label {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.sf-product__logos-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.sf-product__logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.65rem;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font: inherit;
  line-height: 1.2;
}

.sf-product__logo:hover {
  border-color: rgba(0, 0, 0, 0.38);
}

/* The selected state is carried by a 2px ring AND a background tint, not by
   color alone -- a single hue is invisible to some shoppers and unreadable
   in high-contrast mode. */
.sf-product__logo.is-selected {
  border-color: #133752;
  box-shadow: inset 0 0 0 1px #133752;
  background: rgba(19, 55, 82, 0.06);
}

.sf-product__logo:focus-visible {
  outline: 2px solid #133752;
  outline-offset: 2px;
}

/* Fixed-aspect tile sized for the ~3:1 chest-logo art. */
.sf-product__logo-tile {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 84px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  flex: 0 0 auto;
}

.sf-product__logo-tile.is-dark {
  background: #333;
}

.sf-product__logo-tile.is-light {
  background: #f2f2f2;
}

.sf-product__logo-tile.is-neutral {
  background: #e6e6e6;
}

.sf-product__logo-art {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.sf-product__matrix {
  margin-bottom: 18px;
}

.sf-product__matrix-label {
  font-size: 13px;
  margin-bottom: 10px;
}

.sf-product__matrix-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(56px, 1fr));
  gap: 8px;
}

/* A one-size product renders a SINGLE quantity input with no size label
   above it. Left to the auto-fit grid that lone cell would stretch to the
   full width of the buy box -- a ~340px-wide box for a two-digit number,
   which reads as a layout bug rather than as a deliberate simplification.
   Pin it to one input-sized column instead. */
.sf-product__matrix.is-single-size .sf-product__matrix-grid {
  grid-template-columns: 84px;
}

.sf-product__matrix-size {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  color: var(--tf-fg-2, #424242);
  padding: 7px 0;
  text-align: center;
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 8px;
  margin-bottom: 6px;
}

/* Bigger than the previous 12.5px: the abbreviation is what the shopper
   scans, so it leads the cell (Jake, 2026-08-28). */
.sf-product__matrix-size-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
}

/* Secondary by design -- smaller and NON-bold, so the price reads as
   supporting detail under the abbreviation rather than competing with
   it. A size with no price renders no element at all (v-if), never a
   fabricated "$0.00". */
.sf-product__matrix-size-price {
  font-size: 11px;
  font-weight: 400;
  line-height: 1.2;
  color: var(--tf-fg-3, #757575);
}

/* A size the server has no price for (2026-09-29): muted, not an error --
   the shopper simply cannot pick it. */
.sf-product__size-unavailable {
  font-size: 11px;
  font-weight: 400;
  line-height: 1.2;
  color: var(--tf-fg-3, #757575);
  font-style: italic;
}

.sf-product__size-low-stock {
  font-size: 11px;
  font-weight: 400;
  line-height: 1.2;
  color: var(--tf-fg-3, #757575);
}

.sf-product__unavailable {
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--tf-fg-3, #757575);
}

.sf-product__estimate {
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--tf-fg-3, #757575);
}

/* The hover target for the quantity control's availability tooltip. A
   disabled input or stepper fires no hover events, so the tooltip hangs off
   this wrapper instead. Block so the control keeps its full width. */
.sf-product__qty-wrap {
  display: block;
}

.sf-product__matrix-input {
  width: 100%;
  text-align: center;
  padding: 8px 4px;
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  color: var(--tf-fg-1, #212121);
}

.sf-product__matrix-input.is-set {
  border-color: var(--tf-primary, #119ae9);
}

.sf-product__add {
  width: 100%;
  padding: 15px;
  border: none;
  border-radius: 10px;
  background: var(--tf-primary, #119ae9);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-transform: uppercase;
}

.sf-product__add:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* The added state: green for money, and LEGIBLY green.
 *
 * WHICH GREEN, and why not the obvious one. `--tf-success` (#00d084) is the
 * token literally commented "green" and it is 2.03:1 against white -- a
 * filled button with a white label at that ratio is close to unreadable, and
 * it fails both the WCAG AA text minimum (4.5:1) and the non-text/graphical
 * minimum (3:1). `--tf-success-dark` (#2e7d32) carries the white label at
 * 5.13:1 and reads as a shape on the white buy-box at 5.13:1 as well. Same
 * choice, same reason, as the distributor/superadmin Help affordances.
 *
 * Deliberately NOT the storefront category band's green: that band is derived
 * per-store from the distributor's BRAND color (storefrontNavBarTheme), so on
 * most stores it is not green at all, and on a store whose brand IS green the
 * button would dissolve into the chrome. "Added" is a semantic state, not
 * brand expression, so it takes the semantic token -- which also means every
 * store gets the same, measured, AA-clearing green.
 *
 * The label ink is restated rather than inherited so the contrast pair is
 * visible in one place, right next to the fill it is measured against. */
.sf-product__add.is-added {
  background: var(--tf-success-dark, #2e7d32);
  color: #fff;
}
.sf-product__add.is-added:hover {
  /* DARKER on hover, not lighter: lightening a green fill walks it back
     toward the sub-AA end of the scale. --tf-success-darker is 7.16:1. */
  background: var(--tf-success-darker, #256428);
}

/* Keep Shopping: directly below the button, and deliberately quiet. It is the
   secondary action next to a primary one; giving it a second button's weight
   would make the pair a choice rather than an action with an escape. */
.sf-product__keep-shopping {
  display: block;
  margin-top: 12px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  /* --tf-fg-quiet, not a raw grey: the shared quiet-foreground step is the
     one held to a contrast floor by styles/__tests__/quiet-fg-contrast. */
  color: var(--tf-fg-quiet, #6b6b6b);
  text-decoration: underline;
}
.sf-product__keep-shopping:hover {
  /* --tf-fg-1 (#1c1c1c, 14.69:1 on the worst surface token) -- the darkest
     step, so hover only ever increases contrast. */
  color: var(--tf-fg-1, #1c1c1c);
}

.sf-product__note {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px;
  border-radius: 10px;
  background: var(--tf-surface-light, #f5f8fa);
  border: 1px solid var(--tf-border, #e0e0e0);
  font-size: 13px;
  color: var(--tf-fg-2, #424242);
}

.sf-product__features {
  margin-top: 22px;
  border-top: 1px solid var(--tf-border, #e0e0e0);
  padding-top: 18px;
}

.sf-product__features-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 17px;
  font-weight: 600;
  color: var(--tf-fg-1, #212121);
}

.sf-product__features-chevron {
  transition: transform 0.2s;
  color: var(--tf-fg-3, #616161);
}

.sf-product__features-chevron.is-open {
  transform: rotate(180deg);
}

.sf-product__features-body {
  margin-top: 14px;
}

.sf-product__lede {
  font-size: 14px;
  line-height: 1.55;
  color: var(--tf-fg-2, #424242);
  margin: 0 0 16px;
}

.sf-product__feature-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sf-product__feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  font-weight: 500;
}

.sf-product__related {
  border-top: 1px solid var(--tf-border, #e0e0e0);
  padding-top: 8px;
  /* Section spacing (item 3) consistent with Home/Category's 40px rhythm,
     now that this rail sits inside the shared .sf-product__body container
     rather than owning its own side padding. */
  margin-top: 32px;
}

.sf-product__related-title {
  font-size: 20px;
  font-weight: 700;
  padding: 20px 0 4px;
  margin: 0;
}

/* One full-width row per size (decision 1). Rendered only at the mobile
   breakpoint -- see the `isMobile` flag; this is a different DOM from the
   desktop grid, not a restyling of it. */
.sf-product__size-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sf-product__size-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
}

.sf-product__size-row-meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.sf-product__size-row-label {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
}

.sf-product__size-row-price {
  font-size: 12px;
  font-weight: 400;
  line-height: 1.2;
  color: var(--tf-fg-3, #757575);
}

/* The stepper is the fixed-width half of the row; the label takes the
   slack, so a long size name never squeezes the tap targets. */
.sf-product__size-row-stepper {
  flex: 0 0 auto;
  width: 172px;
}

/* Mobile: stack to one column; the gallery thumbnails become a horizontal
   strip above the main image. */
@media (max-width: 860px) {
  .sf-product__grid {
    grid-template-columns: 1fr;
    gap: 0;
    padding: 8px 16px 20px;
  }

  .sf-product__gallery {
    flex-direction: column-reverse;
    position: static;
  }

  .sf-product__thumbs {
    flex-direction: row;
    overflow-x: auto;
    gap: 8px;
    padding: 10px 0 2px;
  }

  .sf-product__thumb {
    width: 54px;
    height: 54px;
    flex-shrink: 0;
  }

  .sf-product__buybox {
    padding-top: 16px;
  }

  /* The in-flow Add to cart is redundant once the pinned bar carries the
     same control -- two live CTAs with the same label is the ambiguity the
     pinned bar was meant to remove. */
  .sf-product__buybox > .sf-product__add,
  .sf-product__buybox > .sf-product__add + .sf-product__keep-shopping {
    display: none;
  }

  /* Reserve the bar's height so the footer and the last size row are
     never trapped underneath it. Matches the bar's own box: 2 x 12px
     padding + a 48px CTA + the summary line. */
  .sf-product__body {
    padding-bottom: 132px;
  }

  /* THE PIN. Scoped to this media query so desktop keeps the documented
     `min-height: 100%` / no-fixed layout exactly as shipped.

     `fixed`, not `sticky`: the scroll container is StorefrontLayout's
     `.storefront-main`, so a sticky bar would pin to that scroller's
     bottom rather than the viewport's, which is not where a thumb is.

     `dvh`-free by construction -- `bottom: 0` on a fixed element already
     tracks the visual viewport on both platforms. iOS Safari OVERLAYS the
     keyboard rather than resizing, so the bar can still sit behind it;
     that case is device-only and is on Jake's hardware checklist. */
  .sf-product__action-bar--pinned {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
    background: #fff;
    border-top: 1px solid var(--tf-border, #e0e0e0);
    box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
  }

  .sf-product__action-bar-summary {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    font-size: 13.5px;
    color: var(--tf-fg-2, #424242);
  }

  .sf-product__action-bar-total {
    font-size: 16px;
    color: var(--tf-fg-1, #212121);
    font-variant-numeric: tabular-nums;
  }

  .sf-product__action-bar-empty {
    color: var(--tf-fg-3, #757575);
  }

  .sf-product__action-bar-cta {
    margin: 0;
    min-height: 48px;
  }
}
</style>
